import { z } from "zod";
export const Id = z.string().min(1).max(128);
export const Uuid = z.uuid();
export const CardId = z.string().regex(/^c\d{1,3}$/);
export const Seat = z.number().int().min(0).max(3);
export const Version = z.number().int().nonnegative();
/** A seat receiving tribute temporarily holds 28 cards before returning one. */
export const MAX_TABLE_HAND_SIZE = 28;
export const BotDifficulty = z.enum(["standard", "expert", "master"]);
export const VoiceMode = z.enum(["push-to-talk", "open-mic", "muted"]);
const publicAvatarCatalog = {
    woman: {
        face: ["face-w-soft", "face-w-clear", "face-w-defined", "face-w-bright"],
        hair: [
            "hair-w-wave",
            "hair-w-bob",
            "hair-w-tail",
            "hair-w-shoulder",
            "hair-w-bun",
            "hair-w-layer",
            "hair-w-pixie",
            "hair-w-curl",
        ],
        outfit: [
            "outfit-w-jade",
            "outfit-w-cream",
            "outfit-w-navy",
            "outfit-w-rose",
            "outfit-w-sport",
            "outfit-w-copper",
        ],
        accessory: [
            "accessory-w-pearl",
            "accessory-w-glasses",
            "accessory-w-watch",
            "accessory-w-none",
        ],
        idle: ["idle-w-poised", "idle-w-wave", "idle-w-think", "idle-w-relaxed"],
    },
    man: {
        face: ["face-m-soft", "face-m-clear", "face-m-defined", "face-m-bright"],
        hair: [
            "hair-m-texture",
            "hair-m-side",
            "hair-m-crop",
            "hair-m-wave",
            "hair-m-swept",
            "hair-m-layer",
            "hair-m-sport",
            "hair-m-casual",
        ],
        outfit: [
            "outfit-m-navy",
            "outfit-m-jade",
            "outfit-m-cream",
            "outfit-m-grey",
            "outfit-m-sport",
            "outfit-m-copper",
        ],
        accessory: [
            "accessory-m-watch",
            "accessory-m-glasses",
            "accessory-m-headphone",
            "accessory-m-none",
        ],
        idle: ["idle-m-poised", "idle-m-wave", "idle-m-think", "idle-m-relaxed"],
    },
};
/** Public, non-sensitive appearance metadata. Unknown keys are rejected. */
export const AvatarConfig = z
    .object({
    version: z.literal(1),
    gender: z.enum(["woman", "man"]),
    face: z.string().min(1).max(64),
    skin: z.enum(["skin-warm-1", "skin-warm-2", "skin-warm-3", "skin-neutral"]),
    eyes: z.enum(["eyes-clear", "eyes-soft", "eyes-bright"]),
    brows: z.enum(["brows-natural", "brows-straight", "brows-defined"]),
    nose: z.enum(["nose-soft", "nose-defined", "nose-round"]),
    mouth: z.enum(["mouth-smile", "mouth-calm", "mouth-bright"]),
    hair: z.string().min(1).max(64),
    hairColor: z.string().regex(/^#[0-9a-f]{6}$/iu),
    facialDetail: z.enum([
        "detail-soft-blush",
        "detail-freckle",
        "detail-clean",
    ]),
    outfit: z.string().min(1).max(64),
    shoes: z.enum(["shoes-modern-loafer", "shoes-sneaker", "shoes-derby"]),
    accessory: z.string().min(1).max(64),
    idle: z.string().min(1).max(64),
    customPortraitId: z.uuid().optional(),
})
    .strict()
    .superRefine((avatar, context) => {
    const catalog = publicAvatarCatalog[avatar.gender];
    for (const slot of [
        "face",
        "hair",
        "outfit",
        "accessory",
        "idle",
    ]) {
        if (!catalog[slot].includes(avatar[slot])) {
            context.addIssue({
                code: "custom",
                path: [slot],
                message: `Avatar ${slot} does not belong to ${avatar.gender}`,
            });
        }
    }
});
/** Validated compatibility fallback for snapshots created before public avatars were persisted. */
export const DEFAULT_PUBLIC_AVATAR_CONFIG = AvatarConfig.parse({
    version: 1,
    gender: "woman",
    face: "face-w-soft",
    skin: "skin-warm-2",
    eyes: "eyes-clear",
    brows: "brows-natural",
    nose: "nose-soft",
    mouth: "mouth-smile",
    hair: "hair-w-wave",
    hairColor: "#2b1b17",
    facialDetail: "detail-soft-blush",
    outfit: "outfit-w-jade",
    shoes: "shoes-modern-loafer",
    accessory: "accessory-w-pearl",
    idle: "idle-w-poised",
});
export const RuleProfile = z.object({
    id: Id,
    wildcardInStraightFlush: z.boolean().default(true),
    antiTributeBigJokerCount: z.number().int().min(0).max(4).default(2),
    allowA2345Straight: z.boolean().default(true),
    straightFlushBeatsBombSize: z.number().int().min(4).max(10).default(5),
    finishMode: z.enum(["fixed-rounds", "through-ace"]).default("through-ace"),
    fixedRounds: z.number().int().min(1).max(32).default(8),
});
export const RoomSettings = z.object({
    name: z.string().trim().min(1).max(24),
    visibility: z.enum(["private", "public"]),
    passwordProtected: z.boolean(),
    ruleProfile: RuleProfile,
    turnSeconds: z.number().int().min(10).max(120),
    allowBots: z.boolean(),
    botDifficulty: BotDifficulty,
    allowSpectators: z.boolean(),
    allowPause: z.boolean(),
    voiceMode: VoiceMode,
});
const ActionBase = z.object({
    actionId: Uuid,
    expectedVersion: Version,
    roomId: Id,
});
export const ClientAction = z.discriminatedUnion("type", [
    ActionBase.extend({
        type: z.literal("JOIN_ROOM"),
        inviteToken: z.string().max(512).optional(),
    }),
    ActionBase.extend({ type: z.literal("READY"), ready: z.boolean() }),
    ActionBase.extend({ type: z.literal("START_GAME") }),
    ActionBase.extend({
        type: z.literal("PLAY_CARDS"),
        cardIds: z.array(CardId).min(1).max(27),
    }),
    ActionBase.extend({ type: z.literal("PASS") }),
    ActionBase.extend({ type: z.literal("OFFER_TRIBUTE"), cardId: CardId }),
    ActionBase.extend({
        type: z.literal("RETURN_TRIBUTE"),
        to: Seat,
        cardId: CardId,
    }),
    ActionBase.extend({ type: z.literal("PAUSE") }),
    ActionBase.extend({ type: z.literal("RESUME") }),
    ActionBase.extend({ type: z.literal("SET_TRUSTEE"), enabled: z.boolean() }),
    ActionBase.extend({
        type: z.literal("ADD_BOT"),
        seat: Seat,
        difficulty: BotDifficulty,
    }),
    ActionBase.extend({ type: z.literal("REMOVE_BOT"), seat: Seat }),
    ActionBase.extend({
        type: z.literal("QUICK_PHRASE"),
        phraseId: z.enum([
            "hello",
            "nice-play",
            "thanks",
            "one-moment",
            "sorry",
            "network-slow",
            "again",
        ]),
    }),
    ActionBase.extend({
        type: z.literal("EMOTE"),
        emoteId: z.enum(["smile", "clap", "thumbs-up", "thinking"]),
    }),
    ActionBase.extend({ type: z.literal("HOST_MUTE_TABLE"), muted: z.boolean() }),
    ActionBase.extend({
        type: z.literal("REQUEST_RECONNECT"),
        lastEventSeq: z.number().int().min(-1),
    }),
]);
export const PublicPlayer = z
    .object({
    userId: Id,
    nickname: z.string().min(1).max(24),
    seat: Seat,
    team: z.number().int().min(0).max(1),
    remainingCards: z.number().int().min(0).max(MAX_TABLE_HAND_SIZE),
    connected: z.boolean(),
    isBot: z.boolean(),
    finishOrder: z.number().int().min(1).max(4).nullable(),
    voiceActive: z.boolean(),
    /** Null only when no human profile owns the seat (for example a pure AI). */
    avatar: AvatarConfig.nullable(),
})
    .superRefine((player, context) => {
    if (!player.isBot && player.avatar === null) {
        context.addIssue({
            code: "custom",
            path: ["avatar"],
            message: "Human-controlled seats require a public avatar",
        });
    }
});
export const PublicPlay = z.object({
    seat: Seat,
    cardIds: z.array(CardId),
    combination: z.string().min(1),
    turnIndex: z.number().int().nonnegative(),
});
export const ViewerSnapshot = z.object({
    roomId: Id,
    ownerUserId: Id,
    tableMuted: z.boolean(),
    version: Version,
    phase: z.enum(["waiting", "tribute", "playing", "settled"]),
    players: z.array(PublicPlayer).max(4),
    selfSeat: Seat.nullable(),
    selfHand: z.array(CardId).max(MAX_TABLE_HAND_SIZE),
    currentSeat: Seat.nullable(),
    deadlineAt: z.number().int().nullable(),
    /** Server clock sampled with this snapshot, for drift-corrected countdowns. */
    serverNow: z.number().int().optional(),
    levelRank: z.string().min(1).max(2),
    teamLevels: z
        .tuple([z.string().min(1).max(2), z.string().min(1).max(2)])
        .optional(),
    aceFailureCounts: z
        .tuple([z.number().int().min(0).max(2), z.number().int().min(0).max(2)])
        .optional(),
    lastPlay: PublicPlay.nullable(),
    passCount: z.number().int().min(0).max(3),
    paused: z.boolean().optional(),
    tributeAction: z
        .object({
        kind: z.enum(["offer", "return"]),
        actorSeat: Seat,
        toSeat: Seat.nullable(),
    })
        .nullable()
        .optional(),
    eventSeq: z.number().int().min(-1),
});
/** Public seat projection for authenticated, read-only spectators. */
export const SpectatorPublicPlayer = z
    .object({
    publicId: z.string().regex(/^seat-[0-3]$/u),
    nickname: z.string().min(1).max(24),
    seat: Seat,
    team: z.number().int().min(0).max(1),
    remainingCards: z.number().int().min(0).max(MAX_TABLE_HAND_SIZE),
    connected: z.boolean(),
    isBot: z.boolean(),
    finishOrder: z.number().int().min(1).max(4).nullable(),
    avatar: AvatarConfig.nullable(),
})
    .strict()
    .superRefine((player, context) => {
    if (player.publicId !== `seat-${player.seat}`) {
        context.addIssue({
            code: "custom",
            path: ["publicId"],
            message: "Public seat identifier must match seat",
        });
    }
    if (!player.isBot && player.avatar === null) {
        context.addIssue({
            code: "custom",
            path: ["avatar"],
            message: "Human-controlled seats require a public avatar",
        });
    }
});
/**
 * Spectators receive an explicitly redacted projection. Identity IDs, table
 * audio state, and every private hand are intentionally absent.
 */
export const SpectatorSnapshot = z
    .object({
    roomId: Id,
    ownerSeat: Seat,
    version: Version,
    phase: z.enum(["tribute", "playing", "settled"]),
    players: z.array(SpectatorPublicPlayer).length(4),
    selfSeat: z.null(),
    selfHand: z.array(CardId).length(0),
    currentSeat: Seat.nullable(),
    deadlineAt: z.number().int().nullable(),
    serverNow: z.number().int(),
    levelRank: z.string().min(1).max(2),
    aceFailureCounts: z
        .tuple([z.number().int().min(0).max(2), z.number().int().min(0).max(2)])
        .optional(),
    lastPlay: PublicPlay.nullable(),
    passCount: z.number().int().min(0).max(3),
    paused: z.boolean(),
    tributeAction: z
        .object({
        kind: z.enum(["offer", "return"]),
        actorSeat: Seat,
        toSeat: Seat.nullable(),
    })
        .strict()
        .nullable(),
    eventSeq: z.number().int().min(-1),
})
    .strict();
export const PublicEvent = z.object({
    seq: z.number().int().nonnegative(),
    version: Version,
    type: z.enum([
        "PLAYER_JOINED",
        "PLAYER_READY",
        "GAME_STARTED",
        "ROUND_STARTED",
        "CARDS_PLAYED",
        "PLAYER_PASSED",
        "PLAYER_FINISHED",
        "TRICK_RESET",
        "ROUND_SETTLED",
        "LEVEL_ADVANCED",
        "ACE_RESET",
        "TRIBUTE_OFFERED",
        "TRIBUTE_RECEIVED",
        "TRIBUTE_RETURNED",
        "ANTI_TRIBUTE",
        "MATCH_SETTLED",
        "GAME_PAUSED",
        "GAME_RESUMED",
        "CONTROL_CHANGED",
        "QUICK_PHRASE",
        "EMOTE",
    ]),
    payload: z.record(z.string(), z.unknown()),
    createdAt: z.number().int(),
});
const SpectatorEventFields = {
    seq: z.number().int().nonnegative(),
    version: Version,
    createdAt: z.number().int(),
};
/** Every spectator event payload is allowlisted field-by-field. */
export const SpectatorEvent = z.discriminatedUnion("type", [
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("GAME_STARTED"),
        payload: z.object({}).strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("ROUND_STARTED"),
        payload: z
            .object({
            roundNumber: z.number().int().positive(),
            dealerSeat: Seat,
            firstSeat: Seat.nullable().optional(),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("CARDS_PLAYED"),
        payload: z
            .object({ seat: Seat, cardIds: z.array(CardId).min(1).max(27) })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("PLAYER_PASSED"),
        payload: z.object({ seat: Seat }).strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("PLAYER_FINISHED"),
        payload: z
            .object({ seat: Seat, order: z.number().int().min(1).max(4) })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("TRICK_RESET"),
        payload: z.object({ leadSeat: Seat }).strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("ROUND_SETTLED"),
        payload: z
            .object({
            roundNumber: z.number().int().positive(),
            finishOrder: z.array(Seat).length(4),
            winningTeam: z.number().int().min(0).max(1),
            advancement: z.number().int().min(1).max(3),
            doubleUp: z.boolean(),
            levelBefore: z.string().min(1).max(2),
            levelAfter: z.string().min(1).max(2),
            passedAce: z.boolean(),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("LEVEL_ADVANCED"),
        payload: z
            .object({
            winningTeam: z.number().int().min(0).max(1),
            advancement: z.number().int().min(1).max(3),
            levelBefore: z.string().min(1).max(2),
            levelAfter: z.string().min(1).max(2),
            passedAce: z.boolean(),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("ACE_RESET"),
        payload: z
            .object({
            team: z.number().int().min(0).max(1),
            failedAttempts: z.literal(3),
            levelBefore: z.literal("A"),
            levelAfter: z.literal("2"),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("MATCH_SETTLED"),
        payload: z
            .object({
            teamLevels: z.tuple([
                z.string().min(1).max(2),
                z.string().min(1).max(2),
            ]),
            teamRoundWins: z.tuple([
                z.number().int().nonnegative(),
                z.number().int().nonnegative(),
            ]),
            teamPoints: z.tuple([
                z.number().int().nonnegative(),
                z.number().int().nonnegative(),
            ]),
            winningTeam: z.number().int().min(0).max(1),
            score: z.number().int().min(1).max(3),
            levelDelta: z.number().int().min(1).max(3),
            finishMode: z.enum(["fixed-rounds", "through-ace"]),
            outcomeBasis: z.enum([
                "through-ace",
                "final-level",
                "round-wins",
                "team-points",
                "final-round-tiebreak",
            ]),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("TRIBUTE_OFFERED"),
        payload: z
            .object({
            from: Seat,
            cardId: CardId,
            tributeKind: z.enum(["single", "double", "inner"]),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("TRIBUTE_RECEIVED"),
        payload: z
            .object({
            from: Seat,
            to: Seat,
            cardId: CardId,
            tributeKind: z.enum(["single", "double", "inner"]),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("TRIBUTE_RETURNED"),
        payload: z
            .object({
            from: Seat,
            to: Seat,
            cardId: CardId,
            tributeKind: z.enum(["single", "double", "inner"]),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("ANTI_TRIBUTE"),
        payload: z
            .object({ donors: z.array(Seat).min(1).max(2), firstSeat: Seat })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("GAME_PAUSED"),
        payload: z.object({ seat: Seat }).strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("GAME_RESUMED"),
        payload: z.object({ seat: Seat }).strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("CONTROL_CHANGED"),
        payload: z
            .object({
            seat: Seat,
            control: z.enum([
                "human",
                "bot",
                "bot-takeover",
                "pending-human-return",
                "empty",
            ]),
            reason: z.string().min(1).max(32),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("QUICK_PHRASE"),
        payload: z
            .object({
            seat: Seat,
            phraseId: z.enum([
                "hello",
                "nice-play",
                "thanks",
                "one-moment",
                "sorry",
                "network-slow",
                "again",
            ]),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        ...SpectatorEventFields,
        type: z.literal("EMOTE"),
        payload: z
            .object({
            seat: Seat,
            emoteId: z.enum(["smile", "clap", "thumbs-up", "thinking"]),
        })
            .strict(),
    })
        .strict(),
]);
/**
 * The server has scanned every source event through `throughSeq`. The public
 * list can contain gaps because identity-only and private source events are
 * deliberately omitted from spectator projections.
 */
export const SpectatorEventBatch = z
    .object({
    events: z.array(SpectatorEvent),
    throughSeq: z.number().int().min(-1).max(Number.MAX_SAFE_INTEGER),
})
    .strict()
    .superRefine((batch, context) => {
    let previousSeq = -1;
    for (let index = 0; index < batch.events.length; index += 1) {
        const event = batch.events[index];
        if (!event)
            continue;
        if (event.seq <= previousSeq) {
            context.addIssue({
                code: "custom",
                path: ["events", index, "seq"],
                message: "Spectator events must be strictly ordered",
            });
        }
        if (event.seq > batch.throughSeq) {
            context.addIssue({
                code: "custom",
                path: ["events", index, "seq"],
                message: "Spectator event exceeds source watermark",
            });
        }
        previousSeq = event.seq;
    }
});
export const SpectatorServerMessage = z.discriminatedUnion("type", [
    z
        .object({ type: z.literal("SNAPSHOT"), snapshot: SpectatorSnapshot })
        .strict(),
    z.object({ type: z.literal("EVENT"), event: SpectatorEvent }).strict(),
]);
export const ServerMessage = z.discriminatedUnion("type", [
    z.object({ type: z.literal("ACK"), actionId: Uuid, version: Version }),
    z.object({ type: z.literal("SNAPSHOT"), snapshot: ViewerSnapshot }),
    z.object({ type: z.literal("EVENT"), event: PublicEvent }),
    z.object({
        type: z.literal("ERROR"),
        actionId: Uuid.optional(),
        code: z.enum([
            "UNAUTHENTICATED",
            "FORBIDDEN",
            "ROOM_NOT_FOUND",
            "INVALID_ACTION",
            "INVALID_CARDS",
            "NOT_YOUR_TURN",
            "STALE_VERSION",
            "RATE_LIMITED",
            "ROOM_FULL",
            "INTERNAL",
        ]),
        message: z.string(),
        snapshot: ViewerSnapshot.optional(),
    }),
    z.object({
        type: z.literal("PONG"),
        sentAt: z.number().int(),
        serverAt: z.number().int(),
    }),
    z.object({
        type: z.literal("AUDIO_GRANT"),
        serverUrl: z.url(),
        roomName: Id,
        participantId: Id,
        token: z.string().min(16),
        expiresAt: z.number().int(),
    }),
]);
export const RevealedHands = z
    .object({
    "0": z.array(CardId),
    "1": z.array(CardId),
    "2": z.array(CardId),
    "3": z.array(CardId),
})
    .strict();
export const RevealedHandsByRound = z.record(z.string().regex(/^(?:[1-9]|[12]\d|3[0-2])$/u), RevealedHands);
export const ReplayManifest = z
    .object({
    matchId: Id,
    final: z.boolean(),
    participantAuthorized: z.boolean(),
    events: z.array(PublicEvent),
    revealedHandsByRound: RevealedHandsByRound.optional(),
    /** Backward-compatible reader for matches persisted before round manifests. */
    revealedHands: RevealedHands.optional(),
    checksum: z.string().min(16),
})
    .superRefine((value, context) => {
    if ((value.revealedHandsByRound || value.revealedHands) &&
        (!value.final || !value.participantAuthorized)) {
        context.addIssue({
            code: "custom",
            message: "Hands may only be revealed to participants after final settlement",
            path: [
                value.revealedHandsByRound ? "revealedHandsByRound" : "revealedHands",
            ],
        });
    }
});
//# sourceMappingURL=index.js.map